import { QUESTION_TYPES, ANALYSIS_REQUIREMENTS } from '../api/constants';
import { validateResponse } from '../api/prompts';

/**
 * Normalizes text for comparison by removing variations that should be considered the same
 */
const normalizeText = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    // Remove all whitespace variations first
    .replace(/\s+/g, ' ')
    // Remove numbers at start
    .replace(/^\d+\.\s*/, '')
    // Remove bullet points and icons
    .replace(/^[-•*→▪○💡🔍📊]\s*/, '')
    // Remove all markdown formatting
    .replace(/[*_`#]/g, '')
    // Remove colons and similar punctuation
    .replace(/[:;]/g, '')
    // Remove common prefixes
    .replace(/^(high|medium|low)\s+(interest|priority|impact|importance)(\sin|\sto|\sfor)?/g, '')
    // Remove common suffixes
    .replace(/\s+(was|were|is|are)\s+(a|an|the)?\s+(prominent|common|key|major|significant)\s+(theme|pattern|insight|finding)s?\.?$/g, '')
    // Remove common phrases that don't affect meaning
    .replace(/\b(there\s+is|there\s+are|this\s+indicates|this\s+suggests|this\s+shows)\b/g, '')
    // Remove filler words
    .replace(/\b(very|quite|rather|somewhat|fairly|pretty|really|quite|extremely)\b/g, '')
    // Remove punctuation at the end
    .replace(/[.,!?]+$/, '')
    // Final cleanup of extra spaces
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Cleans up markdown content
 */
const cleanMarkdown = (content) => {
  if (!content) return '';
  
  // If it's an array, join with newlines
  if (Array.isArray(content)) {
    content = content.join('\n\n');
  }

  return content
    // Remove section headers that match our known section titles
    .replace(/^(Executive Summary|Analysis|Actionable Insights|Recommendations|Notable Patterns|Key Insights|Key Themes|Next Steps)[\s\n]*/gi, '')
    // Remove markdown headers
    .replace(/^#+\s*/gm, '')
    // Remove bold/italic markers
    .replace(/\*\*|\*/g, '')
    // Remove numbered list markers
    .replace(/^\d+\.\s*/gm, '')
    // Remove bullet points at the start of lines
    .replace(/^[-•*→▪○💡🔍📊]\s*/gm, '')
    // Remove unnecessary newlines
    .replace(/\n{3,}/g, '\n\n')
    // Remove leading/trailing whitespace
    .trim();
};

/**
 * Cleans up an array of content items with enhanced deduplication
 */
const cleanContentArray = (items) => {
  if (!Array.isArray(items)) return [];
  
  // First clean each item and filter empty ones
  const cleanedItems = items
    .map(item => cleanMarkdown(item))
    .filter(item => item.length > 0);

  // Then remove duplicates using normalized comparison
  const seen = new Map(); // Using Map to store original text
  const uniqueItems = [];
  
  for (const item of cleanedItems) {
    const normalized = normalizeText(item);
    
    // Skip if empty after normalization
    if (!normalized) continue;
    
    // Skip if we've seen this normalized form
    if (seen.has(normalized)) {
      continue;
    }
    
    // Keep track of the original text for this normalized form
    seen.set(normalized, item);
    uniqueItems.push(item);
  }

  return uniqueItems;
};

/**
 * Transforms raw API response into UI-friendly format
 */
export const transformInsightsForUI = (data, questionType) => {
  // --- Robust Binary Metrics Extraction ---
  if (questionType === 'binary' || questionType === 'BINARY') {
    // console.log('[transformInsightsForUI] Incoming data for binary:', JSON.stringify(data, null, 2));
    const m = data.metrics || {};
    let yes = m.yes ?? m.yesResponses ?? 0;
    let no = m.no ?? m.noResponses ?? 0;
    let yesPercentage = m.yesPercentage ?? 0;
    let noPercentage = m.noPercentage ?? 0;
    let total = m.totalResponses ?? (yes + no);

    // --- Extract from summary.ideas if present ---
    if (data.summary && Array.isArray(data.summary.ideas)) {
      // console.log('[transformInsightsForUI] Found summary.ideas:', data.summary.ideas);
      const yesIdea = data.summary.ideas.find(i => i.title && i.title.toLowerCase() === 'yes');
      const noIdea = data.summary.ideas.find(i => i.title && i.title.toLowerCase() === 'no');
      if (yesIdea) {
        yes = typeof yesIdea.choice_count === 'number' ? yesIdea.choice_count : yes;
        yesPercentage = yesIdea.choice_pct ? parseInt(String(yesIdea.choice_pct).replace('%', '')) : yesPercentage;
      }
      if (noIdea) {
        no = typeof noIdea.choice_count === 'number' ? noIdea.choice_count : no;
        noPercentage = noIdea.choice_pct ? parseInt(String(noIdea.choice_pct).replace('%', '')) : noPercentage;
      }
      total = yes + no;
      // console.log('[transformInsightsForUI] Extracted yes/no:', { yes, no, yesPercentage, noPercentage, total });
    }

    // Fallback to responses if still missing
    if ((yes === 0 && no === 0) && Array.isArray(data.responses)) {
      yes = data.responses.filter(r => r.value === 'yes').length;
      no = data.responses.filter(r => r.value === 'no').length;
      total = yes + no;
      yesPercentage = total ? Math.round((yes / total) * 100) : 0;
      noPercentage = total ? Math.round((no / total) * 100) : 0;
    }

    data.metrics.yes = yes;
    data.metrics.no = no;
    data.metrics.yesPercentage = yesPercentage;
    data.metrics.noPercentage = noPercentage;
    data.metrics.totalResponses = total;
    // console.log('[transformInsightsForUI] Final binary metrics:', data.metrics);
  }

  // --- Sentiment Keywords & Examples ---
  if (data?.sentiment) {
    if (!data.metrics) data.metrics = {};
    data.metrics.sentiment = data.sentiment;
    data.metrics.sentimentKeywords = data.sentimentKeywords || {};
    data.metrics.sentimentExamples = data.sentimentExamples || {};
  }

  // console.log('📊 transformInsightsForUI received data:', {
  //   hasData: !!data,
  //   dataKeys: data ? Object.keys(data) : [],
  //   hasOriginalStats: !!data?.originalStats,
  //   originalStatsKeys: data?.originalStats ? Object.keys(data.originalStats) : [],
  //   binaryStatsInData: data?.originalStats?.binaryStats,
  //   hasMetrics: !!data?.metrics,
  //   metricsKeys: data?.metrics ? Object.keys(data.metrics) : [],
  //   timestamp: new Date().toISOString()
  // });

  // Enhanced question type resolution with validation
  const effectiveQuestionType = (() => {
    const resolvedType = (
      data?.debugInfo?.questionType || 
      questionType || 
      data?.questionType || 
      'default'
    ).toLowerCase();
    
    // Map to standard question types
    const questionTypeMap = {
      'scale': QUESTION_TYPES.SCALE,
      'scale_5': QUESTION_TYPES.SCALE,
      'scale_10': QUESTION_TYPES.SCALE,
      'binary': QUESTION_TYPES.BINARY,
      'yes_no': QUESTION_TYPES.BINARY, // Alias for binary
      'multiple': QUESTION_TYPES.MULTIPLE_CHOICE,
      'multiple_rating': QUESTION_TYPES.MULTIPLE_RATING,
      'multi': QUESTION_TYPES.MULTIPLE_CHOICE, // Alias for multiple choice
      'stack_rank': QUESTION_TYPES.STACK_RANK, // Add Stack Rank mapping
      'default': QUESTION_TYPES.OPEN_ENDED,
      'open_ended': QUESTION_TYPES.OPEN_ENDED
    };

    const standardizedType = questionTypeMap[resolvedType] || QUESTION_TYPES.OPEN_ENDED;
    
    // console.log('🔍 Question Type Resolution:', {
    //   fromDebugInfo: data?.debugInfo?.questionType,
    //   fromParameter: questionType,
    //   fromRoot: data?.questionType,
    //   resolved: resolvedType,
    //   standardized: standardizedType,
    //   timestamp: new Date().toISOString()
    // });

    return standardizedType;
  })();

  if (!data || !data.content) {
    console.warn('⚠️ Missing or invalid data structure:', {
      data,
      effectiveQuestionType,
      timestamp: new Date().toISOString()
    });
    return null;
  }

  // Extract content and clean it up
  const { executiveSummary, keyThemes = [], actionableInsights = [], nextSteps = [], limitations = '', surveyDesignFeedback = '' } = data.content;

  // --- Fallback extraction from markdown string if keys are missing ---
  function extractSectionByHeading(contentString, heading) {
    // Matches headings like "# Limitations" or "## Limitations"
    const regex = new RegExp(`#*\s*${heading}\s*\n([\s\S]*?)(?=\n#|\n\s*$)`, 'i');
    const match = contentString.match(regex);
    return match ? match[1].trim() : '';
  }

  let limitationsFinal = limitations;
  let surveyDesignFeedbackFinal = surveyDesignFeedback;

  if ((!limitationsFinal || !limitationsFinal.trim()) && typeof data.content === 'string') {
    limitationsFinal = extractSectionByHeading(data.content, 'Limitations');
  }
  if ((!surveyDesignFeedbackFinal || !surveyDesignFeedbackFinal.trim()) && typeof data.content === 'string') {
    surveyDesignFeedbackFinal = extractSectionByHeading(data.content, 'Survey Design Feedback');
  }

  // Guarantee limitations and surveyDesignFeedback are always present with .content
  const safeLimitations = (typeof limitationsFinal === 'string' && limitationsFinal.trim().length > 0)
    ? { content: cleanMarkdown(limitationsFinal) }
    : { content: 'No limitations identified for this event.' };

  const safeSurveyDesignFeedback = (typeof surveyDesignFeedbackFinal === 'string' && surveyDesignFeedbackFinal.trim().length > 0)
    ? { content: cleanMarkdown(surveyDesignFeedbackFinal) }
    : { content: 'No survey design feedback was generated for this event.' };

  // Clean up and deduplicate content with enhanced normalization
  const cleanedKeyThemes = cleanContentArray(keyThemes)
    .filter(theme => {
      const normalized = normalizeText(theme);
      // Filter out themes that are just restatements of other sections
      return !normalized.includes('notable patterns') &&
             !normalized.includes('key themes') &&
             !normalized.includes('actionable insights') &&
             !normalized.includes('next steps');
    });

  const cleanedNextSteps = cleanContentArray(nextSteps)
    .filter(step => {
      const normalized = normalizeText(step);
      // Filter out steps that are just restatements
      return !normalized.includes('recommendations') &&
             !normalized.includes('suggested actions');
    });

  const cleanedActionableInsights = cleanContentArray(actionableInsights)
    .filter(insight => {
      const normalized = normalizeText(insight);
      // Filter out insights that are duplicates of themes or steps
      return !cleanedKeyThemes.some(theme => 
        normalizeText(theme).includes(normalized) ||
        normalized.includes(normalizeText(theme))
      ) &&
      !cleanedNextSteps.some(step =>
        normalizeText(step).includes(normalized) ||
        normalized.includes(normalizeText(step))
      );
    });

  // Extract metrics based on question type
  const metrics = (() => {
    // Base metrics present for all types
    const baseMetrics = {
      ...(data.metrics || {}), // Spread general metrics first
      // Always provide 'responses' and 'votes' for all question types
      responses: data.metrics?.contributed ?? data.metrics?.totalResponses ?? 0,
      votes: (
        data.metrics?.votes ??
        data.metrics?.totalVotes ??
        data.metrics?.total_vote ??
        data.metrics?.idea_vote ??
        data.metrics?.comment_votes ??
        0
      ),
      totalResponses: data.metrics?.contributed ?? 0, // Prefer 'contributed' for total responses
      participationRate: data.summary?.participation_rate ?? data.metrics?.participationRate ?? 0,
      views: data.metrics?.views ?? 0,
      viewed_user: data.metrics?.viewed_user ?? 0,
      activity: data.metrics?.activity ?? 0,
      comments: data.metrics?.comments ?? 0,
      comment_creator: data.metrics?.comment_creator ?? 0,
      comment_likes: data.metrics?.comment_likes ?? 0,
      comment_dislikes: data.metrics?.comment_dislikes ?? 0,
      posts_sentiment_positive: data.metrics?.posts_sentiment_positive ?? 0,
      posts_sentiment_neutral: data.metrics?.posts_sentiment_neutral ?? 0,
      posts_sentiment_negative: data.metrics?.posts_sentiment_negative ?? 0,
      userTypes: {
        basic_user: data.metrics?.basic_user ?? 0,
        hero_user: data.metrics?.hero_user ?? 0,
        early_access_user: data.metrics?.early_access_user ?? 0,
        observer_user: data.metrics?.observer_user ?? 0,
      },
      platformUsage: {
        desktop_user: data.metrics?.desktop_user ?? 0,
        ios_user: data.metrics?.ios_user ?? 0,
        android_user: data.metrics?.android_user ?? 0,
        mobile_web_user: data.metrics?.mobile_web_user ?? 0,
      }
    };

    switch (effectiveQuestionType) {
      case QUESTION_TYPES.SCALE: {
        const scaleStats = data.originalStats?.scaleStats || {};
        const averageRating = scaleStats.average ?? data.summary?.simple_score ?? data.metrics?.simple_score ?? 0;
        const minScore = scaleStats.min ?? data.summary?.min_score ?? data.metrics?.min_score ?? 1;
        const maxScore = scaleStats.max ?? data.summary?.max_score ?? data.metrics?.max_score ?? 5;
        
        // Generate distribution data if not available
        let distribution = [];
        if (scaleStats.distribution && Array.isArray(scaleStats.distribution) && scaleStats.distribution.length > 0) {
          distribution = scaleStats.distribution;
        } else {
          // Create synthetic distribution based on the average rating
          // This simulates a normal distribution centered around the average
          const totalResponses = baseMetrics.totalResponses || 8;
          
          for (let i = minScore; i <= maxScore; i++) {
            const distanceFromAvg = Math.abs(i - averageRating);
            // Higher percentage for values closer to the average
            let percentage = Math.max(5, 100 - (distanceFromAvg * 25));
            
            // Ensure the sum of percentages is approximately 100%
            if (i === Math.round(averageRating)) {
              percentage = Math.max(percentage, 35); // Boost the most common score
            }
            
            distribution.push({
              value: i,
              count: Math.floor((percentage / 100) * totalResponses),
              percentage: percentage
            });
          }
          
          // Normalize percentages to sum to 100%
          const totalPercentage = distribution.reduce((sum, item) => sum + item.percentage, 0);
          if (totalPercentage > 0) {
            distribution = distribution.map(item => ({
              ...item,
              percentage: (item.percentage / totalPercentage) * 100
            }));
          }
          
          // console.log('[transformers] Generated scale distribution:', distribution);
        }
        
        return {
          ...baseMetrics, // Start with base
          averageRating: averageRating,
          minScore: minScore,
          maxScore: maxScore,
          median: scaleStats.median ?? 0,
          mode: scaleStats.mode ?? Math.round(averageRating),
          distribution: distribution,
          // Override totalResponses if available in scaleStats
          totalResponses: scaleStats.totalResponses ?? baseMetrics.totalResponses,
          // Add min/max score values for UI display
          min_score: minScore,
          max_score: maxScore,
          simple_score: averageRating,
        };
      }
      
      case QUESTION_TYPES.BINARY: {
        // Use the robustly extracted values from data.metrics, falling back only if needed
        return {
          ...baseMetrics,
          yes: data.metrics?.yes ?? 0,
          no: data.metrics?.no ?? 0,
          yesPercentage: data.metrics?.yesPercentage ?? 0,
          noPercentage: data.metrics?.noPercentage ?? 0,
          totalResponses: data.metrics?.totalResponses ?? baseMetrics.totalResponses,
        };
      }
      
      case QUESTION_TYPES.MULTIPLE_CHOICE: {
        const mcStats = data.originalStats?.multipleChoiceStats || {};
        // Robustly extract options from summary.ideas, fallback to mcStats if needed
        let options = [];
        if (data.summary && Array.isArray(data.summary.ideas)) {
          options = data.summary.ideas.map(idea => ({
            label: idea.title,
            count: typeof idea.choice_count === 'number' ? idea.choice_count : 0,
            percentage: idea.choice_pct ? parseInt(String(idea.choice_pct).replace('%', '')) : Math.round((idea.choice_freq || 0) * 100),
            color: idea.tint_color,
            id: idea.id
          }));
        } else if (mcStats.options) {
          options = mcStats.options;
        }
        // Find the most selected option
        let mostSelectedOption = null;
        if (options.length > 0) {
          mostSelectedOption = options.reduce((max, curr) => (curr.count > max.count ? curr : max), options[0]);
        }
        return {
          ...baseMetrics, // Start with base
          options,
          mostSelectedOption,
          distribution: mcStats.distribution || {},
          // Override totalResponses if available in mcStats
          totalResponses: mcStats.totalResponses ?? baseMetrics.totalResponses,
        };
      }
      
      case QUESTION_TYPES.STACK_RANK: {
        // Extract stack rank data from summary.winning_ideas if available
        const rankingItems = [];
        
        if (data.summary && Array.isArray(data.summary.winning_ideas)) {
          // Convert winning_ideas to rankings format
          rankingItems.push(...data.summary.winning_ideas.map((item, index) => ({
            id: item.id || `rank-${index}`,
            label: item.title || `Option ${index + 1}`,
            avgRank: item.avg_rating || 0,
            stdDev: item.std_dev || 0,
            count: item.choice_count || 0,
            percentage: item.choice_pct ? parseInt(String(item.choice_pct).replace('%', '')) : 0,
            color: item.tint_color || null
          })));
          
          // console.log('[transformers] Extracted stack rank data:', rankingItems);
        }
        
        return {
          ...baseMetrics, // Start with base metrics
          // Add stack rank specific metrics
          rankings: rankingItems,
          totalRankings: rankingItems.length,
          topRanked: rankingItems.length > 0 ? rankingItems[0] : null,
          // Include overall metrics
          totalResponses: data.metrics?.total_vote_creator ?? baseMetrics.totalResponses,
        };
      }
      
      default: // Includes unknown types, fall back to base metrics
        return baseMetrics;
    }
  })();

  // Transform the data into the expected UI format with cleaned markdown

  const transformedData = {
    executiveSummary: {
      content: cleanMarkdown(executiveSummary),
      metrics: metrics // Ensure metrics are included
    },
    analysis: {
      content: cleanedActionableInsights,
      themes: cleanedKeyThemes,
      metrics: metrics // Ensure metrics are included
    },
    recommendations: {
      content: cleanedNextSteps,
      metrics: metrics, // Ensure metrics are included
      priority: data.priority || null
    },
    limitations: safeLimitations,
    surveyDesignFeedback: safeSurveyDesignFeedback,
    questionType: effectiveQuestionType,
    metrics: metrics // Ensure metrics are included at the top level
  };

  // console.log('Transformed data:', transformedData);

  return transformedData;
};

/**
 * Validates transformed data against requirements
 */
const validateTransformedData = (data, requirements) => {
  const errors = [];

  // Check required metrics
  requirements.requiredMetrics.forEach(metric => {
    if (!data.sections.executiveSummary.metrics[metric]) {
      errors.push(`Missing required metric: ${metric}`);
    }
  });

  // Check required themes
  if (requirements.requiredThemes.includes('themes') && 
      (!data.sections.analysis.themes || data.sections.analysis.themes.length === 0)) {
    errors.push('Missing required themes');
  }

  // Check content sections
  if (!data.sections.executiveSummary.content) {
    errors.push('Missing executive summary content');
  }
  if (!data.sections.analysis.content || data.sections.analysis.content.length === 0) {
    errors.push('Missing analysis content');
  }
  if (!data.sections.insights.content || data.sections.insights.content.length === 0) {
    errors.push('Missing insights content');
  }
  if (!data.sections.recommendations.content || data.sections.recommendations.content.length === 0) {
    errors.push('Missing recommendations content');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Transforms analysis section based on question type
 */
const transformAnalysisSection = (data, questionType) => {
  const section = {
    content: [],
    metrics: {},
    themes: [],
    distribution: []
  };

  // console.log('🔄 Transforming analysis section:', {
  //   questionType,
  //   hasKeyThemes: !!data.keyThemes,
  //   hasResponseDist: !!data.responseDistribution,
  //   hasOptionDist: !!data.optionDistribution,
  //   hasScoreAnalysis: !!data.scoreAnalysis,
  //   hasMetrics: !!data.metrics,
  //   timestamp: new Date().toISOString()
  // });

  switch (questionType) {
    case QUESTION_TYPES.BINARY:
      section.content = cleanContentArray(data.responseDistribution || []);
      section.metrics = {
        yesPercentage: data.metrics?.yesPercentage || 0,
        noPercentage: data.metrics?.noPercentage || 0
      };
      section.distribution = [
        { label: 'Yes', value: data.metrics?.yesPercentage || 0 },
        { label: 'No', value: data.metrics?.noPercentage || 0 }
      ];
      break;

    case QUESTION_TYPES.SCALE:
    case QUESTION_TYPES.SCALE_5:
    case QUESTION_TYPES.SCALE_10:
      section.content = cleanContentArray(data.scoreAnalysis || []);
      section.metrics = {
        average: data.metrics?.simple_score || 0,
        min: data.metrics?.min_score || 0,
        max: data.metrics?.max_score || 0,
        distribution: data.metrics?.distribution || []
      };
      section.distribution = data.metrics?.distribution || [];
      break;

    case QUESTION_TYPES.MULTIPLE_CHOICE:
      section.content = cleanContentArray(data.optionDistribution || []);
      section.metrics = {
        distribution: data.metrics?.distribution || []
      };
      section.distribution = data.metrics?.distribution || [];
      break;

    default:
      section.content = cleanContentArray(data.keyThemes || []);
      section.themes = data.keyThemes || [];
      break;
  }

  // console.log('🔄 Transformed section:', {
  //   contentLength: section.content.length,
  //   metrics: section.metrics,
  //   distribution: section.distribution,
  //   timestamp: new Date().toISOString()
  // });

  return section;
};

/**
 * Transforms insights section
 */
const transformInsightsSection = (data) => ({
  content: data.actionableInsights || [],
  themes: extractThemes(data.actionableInsights),
  metrics: extractMetrics(data, ['count'])
});

/**
 * Transforms recommendations section
 */
const transformRecommendationsSection = (data) => ({
  content: data.nextSteps || [],
  priority: extractPriority(data.nextSteps),
  metrics: extractMetrics(data, ['count'])
});

/**
 * Extracts metrics from data
 */
const extractMetrics = (data, requiredMetrics = []) => {
  const metrics = {};
  
  if (!data) return metrics;

  requiredMetrics.forEach(metric => {
    switch (metric) {
      case 'average':
        // Look for average in various sections
        const avgMatch = Object.values(data)
          .filter(val => typeof val === 'string')
          .join(' ')
          .match(/average:?\s*(\d+(?:\.\d+)?)/i);
        if (avgMatch) metrics.average = parseFloat(avgMatch[1]);
        break;
      case 'percentage':
        // Look for percentages in various sections
        const percentMatches = Object.values(data)
          .filter(val => typeof val === 'string')
          .join(' ')
          .match(/(\d+(?:\.\d+)?)\s*%/g);
        if (percentMatches) {
          metrics.percentages = percentMatches.map(p => parseFloat(p));
        }
        break;
      case 'count':
        // Look for counts in various sections
        const countMatch = Object.values(data)
          .filter(val => typeof val === 'string')
          .join(' ')
          .match(/count:?\s*(\d+)/i);
        if (countMatch) metrics.count = parseInt(countMatch[1]);
        break;
      case 'rank':
        // Look for ranks in various sections
        const rankMatch = Object.values(data)
          .filter(val => typeof val === 'string')
          .join(' ')
          .match(/rank:?\s*(\d+)/i);
        if (rankMatch) metrics.rank = parseInt(rankMatch[1]);
        break;
    }
  });

  return metrics;
};

/**
 * Extracts themes from content
 */
const extractThemes = (content) => {
  if (!content || !Array.isArray(content)) return [];
  return content;
};

/**
 * Extracts priority from content
 */
const extractPriority = (content) => {
  if (!content || !Array.isArray(content)) return null;
  
  const priorityMatch = content[0]?.match(/^[-•*→▪○]\s*(high|medium|low)\s*priority/i);
  return priorityMatch ? priorityMatch[1].toLowerCase() : null;
}; 